import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ImprovementCalculator from "@/components/ImprovementCalculator";
import { Button } from "@/components/ui/button";
import { ArrowRight, Building2, Globe, Ruler, Store } from "lucide-react";

const POPULAR = [
  "Office Fit-out", "Retail Build-out", "Restaurant / Cafe", "Medical / Dental Clinic",
  "Warehouse Fit-out", "Gym / Fitness Studio", "Salon / Spa", "Co-working Space",
];

export default function TenantImprovement() {
  return (
    <div className="min-h-screen bp-grid">
      <Navbar />
      <section className="border-b border-border">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-8">
          <motion.p initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
            className="font-mono2 text-xs uppercase tracking-[0.3em] text-primary">
            <Building2 className="mr-2 inline h-3.5 w-3.5" /> Tenant Improvement Costs
          </motion.p>
          <motion.h1 initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.06 }}
            className="mt-3 max-w-3xl font-heading text-4xl font-extrabold tracking-tighter sm:text-5xl lg:text-6xl">
            Price your commercial fit-out.
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.12 }}
            className="mt-4 max-w-2xl text-lg text-muted-foreground">
            Ballpark tenant-improvement budgets for offices, retail, restaurants, clinics and more — per square foot
            or metre, in your local currency, anywhere on Earth.
          </motion.p>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-12 sm:px-8">
        <ImprovementCalculator category="tenant" />

        <div className="mt-12 grid gap-4 sm:grid-cols-3">
          {[
            { icon: Globe, title: "Any market", desc: "Local rates for 30+ countries and currencies." },
            { icon: Store, title: "Use-case tuned", desc: "Different baselines for office, retail, F&B and medical." },
            { icon: Ruler, title: "Per-area pricing", desc: "See cost per square foot or metre for the leased space." },
          ].map((f, i) => (
            <div key={i} className="border border-border p-6">
              <f.icon className="h-5 w-5 text-primary" />
              <h3 className="mt-3 font-heading text-lg font-semibold">{f.title}</h3>
              <p className="mt-1 text-sm text-muted-foreground">{f.desc}</p>
            </div>
          ))}
        </div>

        <div className="mt-10">
          <p className="font-mono2 text-xs uppercase tracking-[0.2em] text-accent">Popular fit-outs</p>
          <div className="mt-3 flex flex-wrap gap-2">
            {POPULAR.map((p) => (
              <span key={p} className="border border-border px-3 py-1.5 font-mono2 text-xs uppercase tracking-widest text-muted-foreground">{p}</span>
            ))}
          </div>
        </div>

        <div className="mt-12 flex flex-wrap items-center justify-between gap-4 border border-border border-l-2 border-l-primary bg-secondary/20 p-6">
          <div>
            <p className="font-heading text-xl font-bold">Want the insider playbook?</p>
            <p className="text-muted-foreground">VIP members get benchmark price sheets and negotiation levers for fit-outs.</p>
          </div>
          <Link to="/billing" data-testid="tenant-improvement-cta">
            <Button className="rounded-sm bg-primary font-mono2 uppercase tracking-widest text-black hover:bg-primary/90">
              Go VIP <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>
      </section>
      <Footer />
    </div>
  );
}
